/**
 * Turn an axios/backend error into a clear, human-readable message
 * instead of leaking raw backend / framework output.
 *
 * @param {unknown} error
 * @param {string} [fallback]
 * @param {(key: string, options?: object) => string} [t] i18n `t` when available
 */
export function getApiErrorMessage(error, fallback, t) {
  const tr = (key, defaultValue) =>
    (typeof t === "function" ? t(key, { defaultValue }) : defaultValue);
  const defaultFallback = tr("api.error.generic", "Something went wrong. Please try again.");
  const fb = fallback || defaultFallback;
  const res = error?.response;

  if (!res) {
    if (error?.request) {
      return tr("api.error.noConnection", "No connection to the server. Check your network and try again.");
    }
    return error?.message || fb;
  }

  const data = res.data;

  // Our own explicit error string (e.g. "Insufficient stock for: …")
  if (data && typeof data.error === "string" && data.error.trim()) return data.error;

  // Laravel validation errors → first field message
  if (data && data.errors && typeof data.errors === "object") {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
    if (typeof first === "string") return first;
  }

  // Laravel / API message
  if (data && typeof data.message === "string" && data.message.trim()) return data.message;

  // Fall back to something friendly per status
  switch (res.status) {
    case 401:
      return tr("api.error.sessionExpired", "Your session has expired. Please log in again.");
    case 403:
      return tr("api.error.forbidden", "You do not have permission to do that.");
    case 404:
      return tr("api.error.notFound", "The requested item was not found.");
    case 422:
      return tr("api.error.validation", "Some of the entered information is invalid.");
    default:
      return res.status >= 500
        ? tr("api.error.server", "Server error. Please try again in a moment.")
        : fb;
  }
}
